import { useRef, useState } from 'react';
import { Volume2 } from 'lucide-react';
import { VOICE_OPTIONS, OptionsType } from '@/lib/constants';
import { isHidingMonetizationInStores } from '@/lib/detection';

interface VoiceSelectionGridProps {
  gender: 'f' | 'm';
  selectedVoice: string;
  onSelectVoice: (voice: string) => void;
  isSubscriber: boolean;
  onUpgrade: () => void;
}

export function VoiceSelectionGrid({
  gender,
  selectedVoice,
  onSelectVoice,
  isSubscriber,
  onUpgrade,
}: VoiceSelectionGridProps) {
  const [playingVoice, setPlayingVoice] = useState<string | null>(null);
  const voiceAudioRefs = useRef<Record<string, HTMLAudioElement>>({});
  const voicePlayPromiseRefs = useRef<Record<string, Promise<void> | null>>({});

  const genderFilter = (opt: OptionsType) => {
    return (opt.fOnly && gender === 'f')
      || (opt.mOnly && gender === 'm')
      || (!opt.fOnly && !opt.mOnly);
  };

  const playVoiceSample = async (voiceValue: string) => {
    if (playingVoice === voiceValue) {
      const audio = voiceAudioRefs.current[voiceValue];
      const playPromise = voicePlayPromiseRefs.current[voiceValue];

      if (audio && playPromise) {
        // Wait for the play promise to resolve before pausing
        try {
          await playPromise;
          audio.pause();
        } catch (_err) {
          // Play was already rejected, safe to pause
          audio.pause();
        }
      } else if (audio) {
        audio.pause();
      }
      
      setPlayingVoice(null);
      voicePlayPromiseRefs.current[voiceValue] = null;
      return;
    }

    // Stop any currently playing audio
    if (playingVoice) {
      const currentAudio = voiceAudioRefs.current[playingVoice];
      const currentPlayPromise = voicePlayPromiseRefs.current[playingVoice];
      
      if (currentAudio && currentPlayPromise) {
        try {
          await currentPlayPromise;
          currentAudio.pause();
        } catch (_err) {
          currentAudio.pause();
        }
      } else if (currentAudio) {
        currentAudio.pause();
      }
      
      voicePlayPromiseRefs.current[playingVoice] = null;
    }

    // Create or reuse audio element
    let audio = voiceAudioRefs.current[voiceValue];
    if (!audio) {
      audio = new Audio(`/voices/v2/${voiceValue}.mp3`);
      voiceAudioRefs.current[voiceValue] = audio;
      
      audio.onended = () => {
        if (playingVoice === voiceValue) setPlayingVoice(null);
        voicePlayPromiseRefs.current[voiceValue] = null;
      };
      
      audio.onerror = () => {
        if (playingVoice === voiceValue) setPlayingVoice(null);
        voicePlayPromiseRefs.current[voiceValue] = null;
      };
    }

    // Reset audio to beginning
    audio.currentTime = 0;
    setPlayingVoice(voiceValue);
    
    // Play audio with proper promise handling
    const playPromise = audio.play();
    voicePlayPromiseRefs.current[voiceValue] = playPromise;
    
    playPromise.catch(() => {
      setPlayingVoice(null);
      voicePlayPromiseRefs.current[voiceValue] = null;
    });
  };

  return (
    <div className="flex gap-2 sm:gap-4 flex-wrap justify-center">
      {VOICE_OPTIONS.filter(genderFilter).map(opt => {
        const locked = opt.locked && !isSubscriber;
        const isPlaying = playingVoice === opt.value;
        return (
          <div key={opt.value} className={`relative ${locked && isHidingMonetizationInStores() ? 'hidden' : ''}`}>
            <button
              className={`relative w-28 h-24 px-2 pt-10 rounded-lg select-none flex flex-col items-center justify-center border-2 transition-all ${
                selectedVoice === opt.value ? 'border-pink-500' : 'border-zinc-700'
              } ${
                locked ? '' : 'hover:border-pink-400'
              }`}
              onClick={() => locked ? onUpgrade() : onSelectVoice(opt.value)}
              type="button"
            >
              <span className={`font-medium text-white text-sm ${locked ? 'blur-md' : ''}`}>{`${locked ? 'Locked voice' : opt.label}`}</span>
            </button>
            <button
              className={`absolute top-3 left-1/2 -translate-x-1/2 flex items-center justify-center size-8 rounded-full
                bg-zinc-700/90 text-zinc-300 transition-colors z-10 ${
                  locked ? 'blur-sm hover:bg-zinc-700/90' : 'hover:bg-zinc-600/90 hover:text-white'
                }`}
              onClick={() => {
                if (!locked) {
                  playVoiceSample(opt.value);
                  // seperating sample listening from voice selection on mobile tap proved misleading,
                  // so we also select the voice here
                  onSelectVoice(opt.value);
                } else {
                  onUpgrade();
                }
              }}
              type="button"
              title="Play voice sample"
              aria-label="Play voice sample"
            >
              <Volume2 className={`size-4 ${isPlaying ? 'text-pink-400' : ''}`} />
            </button>
          </div>
        );
      })}
    </div>
  );
} 